import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { authAPI, userSettingsAPI } from '@/services/api'

export const useUserStore = defineStore('user', () => {
  const currentLanguage = ref('en')
  const user = ref(null)
  const isAuthenticated = ref(false)
  const loading = ref(false)
  const combo = ref(null)
  const myBets = ref(null)

  // Computed
  const userName = computed(() => user.value?.name || '')
  const userEmail = computed(() => user.value?.email || '')

  function setLanguage(lang) {
    currentLanguage.value = lang
    // Note: Language is now managed in memory only
  }

  async function updateLanguage(lang) {
    currentLanguage.value = lang
    localStorage.setItem('user-language', lang)
    
    // Save to server if user is authenticated
    if (isAuthenticated.value) {
      try {
        await userSettingsAPI.updateSettings({ language: lang })
      } catch (error) {
        console.warn('Could not save language preference to server:', error)
      }
    }
  }

  function setUser(userData) {
    user.value = userData
    isAuthenticated.value = !!userData
  }

  async function logout() {
    loading.value = true
    try {
      await authAPI.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      user.value = null
      isAuthenticated.value = false
      loading.value = false
    }
  }

  // Initialize user from server data
  async function initializeUser() {
    const hasToken = authAPI.isAuthenticated()
    
    if (hasToken) {
      // Get user data from server
      try {
        const response = await authAPI.getCurrentUser()
        if (response.success) {
          setUser(response.data)
        }
      } catch (error) {
        console.error('Failed to initialize user:', error)
        // If token is invalid, clear it
        if (error.response?.status === 401) {
          authAPI.clearAuth()
        }
      }
    }
  }

  // Initialize language (default to English)
  function initializeLanguage() {
    currentLanguage.value = 'en'
  }

  // Check and refresh user data
  async function refreshUser() {
    if (!authAPI.isAuthenticated()) return

    loading.value = true
    try {
      const response = await authAPI.getCurrentUser()
      if (response.success) {
        setUser(response.data)
      }
    } catch (error) {
      console.error('Failed to refresh user:', error)
      // If token is invalid, clear auth data
      if (error.response?.status === 401) {
        authAPI.clearAuth()
        user.value = null
        isAuthenticated.value = false
      }
    } finally {
      loading.value = false
    }
  }

  // Update user balance (used by Game.updateState)
  function updateBalance(newBalance) {
    if (!user.value) return
    
    // Convert string numbers to actual numbers
    let numericBalance = newBalance
    if (typeof newBalance === 'string' && !isNaN(parseFloat(newBalance))) {
      numericBalance = parseFloat(newBalance)
    }
    
    if (typeof numericBalance === 'number' && !isNaN(numericBalance)) {
      user.value.balance = numericBalance
    }
  }

  // Update specific user properties
  function updateUserData(userData) {
    if (user.value && userData) {
      Object.assign(user.value, userData)
    }
  }

  function resetCombo()
  {
    combo.value = null
    saveCombo()
  }

  function startCombo(item)
  {
    combo.value = []
    addToCombo(item)
  }

  function addToCombo(item)
  {
    combo.value.push(item)
    saveCombo()
  }

  function hasCombo()
  {
    return combo.value && combo.value.length > 0
  }

  function removeFromCombo(index)
  {
    if (!combo.value || index < 0 || index >= combo.value.length) return
    combo.value.splice(index, 1)
    saveCombo()
  }

  function saveCombo()
  {
    // save to localStorage
    if (combo.value && combo.value.length > 0) {
      localStorage.setItem('user-combo', JSON.stringify(combo.value))
    } else {
      localStorage.removeItem('user-combo')
    }
  }

  function loadFromLocalStorage()
  {
    const comboData = localStorage.getItem('user-combo')
    if (comboData) {
      try {
        combo.value = JSON.parse(comboData)
      } catch (error) {
        console.error('Failed to parse combo data from localStorage:', error)
      }
    }
  }

  function getMyBets()
  {
    return myBets.value
  }

  function updateMyBets(bets)
  {
    myBets.value = bets
  }

  return { 
    combo,
    currentLanguage, 
    user, 
    isAuthenticated,
    loading,
    userName,
    userEmail,
    setLanguage,
    updateLanguage,
    setUser,
    loadFromLocalStorage,
    logout,
    hasCombo,
    initializeLanguage,
    initializeUser,
    startCombo,
    addToCombo,
    refreshUser,
    removeFromCombo,
    resetCombo,
    updateBalance,
    updateUserData,
    getMyBets,
    updateMyBets
  }
})